import React, { useCallback, useEffect, useState } from 'react';
import { Progress, Select } from 'antd';
import { ReactComponent as DropDownIcon } from '../../assets/images/chevron-down.svg';
const { Option } = Select;

interface Step {
	title: string;
	description?: string | null;
	progress?: number;
}

interface CircularStepsWithDropdownProps {
	steps?: Array<Step>;
	activeStep: number;
	onNext: () => void;
	onPrevious: () => void;
	onStepChange: (step: number) => void;
	disableDropdown?: boolean;
	currentWizard?: number;
	totalStepsInPreviousWizard?: number;
	draftIndex?: number;
	completedDraftIndex?: number;
	isProposerAndViewable?: boolean;
}

const CircularStepsWithDropdown: React.FC<CircularStepsWithDropdownProps> = ({
	steps,
	activeStep,
	onStepChange,
	disableDropdown = false,
	currentWizard = 0,
	draftIndex,
	completedDraftIndex,
	isProposerAndViewable = false,
}) => {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [completedSteps, setCompletedSteps] = useState<
		Record<number, boolean[]>
	>({});

	useEffect(() => {
		if (isProposerAndViewable) return;
		setCompletedSteps((prev) => {
			const updatedSteps = { ...prev };

			if (
				!updatedSteps[currentWizard] ||
				updatedSteps[currentWizard].length !== steps?.length
			) {
				updatedSteps[currentWizard] = Array(steps?.length).fill(
					false
				) as boolean[];
			}

			let presentWizard = 0;

			if (draftIndex && completedDraftIndex) {
				if (currentWizard && draftIndex >= 7) {
					presentWizard = 1;
				}

				// Ensure presentWizard array is initialized
				if (!updatedSteps[presentWizard]) {
					updatedSteps[presentWizard] = Array(steps?.length).fill(
						false
					) as boolean[];
				}

				if (!presentWizard) {
					for (let i = 0; i <= completedDraftIndex; i++) {
						updatedSteps[presentWizard][i] = true;
					}
				} else {
					if (!updatedSteps[presentWizard - 1]) {
						updatedSteps[presentWizard - 1] = Array(steps?.length).fill(
							false
						) as boolean[];
					}

					for (let i = 0; i <= completedDraftIndex; i++) {
						if (i <= 6) {
							updatedSteps[presentWizard - 1][i] = true;
						} else {
							updatedSteps[presentWizard][completedDraftIndex - i] = true;
						}
					}
				}
			}

			// Preserve previously completed steps
			if (prev[currentWizard]) {
				for (let i = 0; i < prev[currentWizard].length; i++) {
					if (prev[currentWizard][i]) {
						updatedSteps[currentWizard][i] = true;
					}
				}
			}

			if (!draftIndex) {
				for (let i = 0; i < activeStep; i++) {
					updatedSteps[currentWizard][i] = true;
				}
			}
			return updatedSteps;
		});
	}, [
		currentWizard,
		activeStep,
		steps?.length,
		completedDraftIndex,
		draftIndex,
		isProposerAndViewable,
	]);

	const wizardCompletedSteps =
		completedSteps[currentWizard] || Array(steps?.length).fill(false);

	const handleDropdownOpenChange = useCallback(
		(open: boolean) => {
			if (!disableDropdown) {
				setDropdownOpen(open);
			}
		},
		[disableDropdown]
	);

	const handleStepSelect = useCallback(
		(step: number) => {
			if (
				isProposerAndViewable ||
				wizardCompletedSteps[step] ||
				step <= activeStep
			) {
				onStepChange(step);
				setDropdownOpen(false);
			}
		},
		[onStepChange, wizardCompletedSteps, activeStep, isProposerAndViewable]
	);

	const calculateDynamicStrokeColor = useCallback(() => {
		if (!steps || activeStep >= steps.length) {
			return {
				'0%': '#d9d9d9',
				'100%': '#d9d9d9',
			};
		}

		const stepCount = steps.length;
		const startPercent = (activeStep / stepCount) * 100;
		const endPercent = ((activeStep + 1) / stepCount) * 100;

		return {
			[`${startPercent.toFixed(1)}%`]: '#003399',
			[`${endPercent.toFixed(1)}%`]: '#003399',
		};
	}, [steps, activeStep]);

	return (
		<div className="w-full">
			<Select
				value={activeStep}
				onChange={onStepChange}
				className="w-full h-auto"
				open={dropdownOpen}
				suffixIcon={null}
				onDropdownVisibleChange={handleDropdownOpenChange}
				disabled={disableDropdown || (currentWizard === 1 && activeStep === 0)}
				dropdownRender={() => (
					<div className="p-2 bg-white h-auto max-h-[50vh] overflow-auto">
						{steps &&
							steps.map((step, index) => (
								<div
									key={index}
									className={`flex items-center gap-5 mb-3 cursor-pointer justify-between rounded-md transition-colors px-2 py-3 ${
										isProposerAndViewable ||
										wizardCompletedSteps[index] ||
										index <= activeStep
											? 'hover:bg-gray-100'
											: 'opacity-50'
									}`}
									onClick={() => handleStepSelect(index)}
									style={{ borderBottom: '1px solid #B7B9BB', borderRadius: 0 }}
								>
									<Progress
										type="circle"
										percent={(steps && steps[activeStep]?.progress) ?? 0}
										size={40}
										strokeWidth={20}
										format={() => (
											<p className="font-sans font-bold text-xs">{`${
												index + 1
											}`}</p>
										)}
										strokeColor="#003399"
										trailColor="#d9d9d9"
										status="normal"
									/>
									<div className="flex-1 break-normal whitespace-normal">
										<strong
											className={`${
												index === activeStep
													? 'text-primary-blue'
													: 'text-gray-500'
											}`}
										>
											{step.title}
										</strong>
									</div>

									<DropDownIcon className="w-5 h-5 transform -rotate-90" />
								</div>
							))}
					</div>
				)}
			>
				<Option value={activeStep}>
					<div className="flex items-center gap-5 h-auto bg-blue-100 p-2 rounded-none">
						<Progress
							type="circle"
							percent={(steps && steps[activeStep]?.progress) ?? 0}
							size={50}
							strokeWidth={20}
							format={() => null}
							strokeColor={calculateDynamicStrokeColor()}
							trailColor="#d9d9d9"
						/>
						<div className="flex-1 min-w-0 break-normal whitespace-normal">
							<p className="text-md font-bold m-0 leading-[25px]">
								<span>
									{activeStep + 1} / {steps && steps.length}{' '}
									{steps && steps[activeStep]?.title}
								</span>
							</p>
							<p className="text-xs leading-5 text-gray-700 w-[90%] break-normal whitespace-normal m-0">
								{steps && steps[activeStep]?.description}
							</p>
						</div>
						<div className="flex items-center">
							<DropDownIcon style={{ width: 20, height: 20 }} />
							{/* Left-aligned arrow */}
						</div>
					</div>
				</Option>
			</Select>
		</div>
	);
};

export default CircularStepsWithDropdown;
